import Modal from '@mui/material/Modal';
import { Button } from '@crono-one/design-system';
import { useEffect, useMemo, useRef, useState } from 'react';
import IconExpand2 from 'crono-fe-common/icons/IconExpand2';
import IconCopy from 'crono-fe-common/icons/Icon-Copy';
import { useSnackbar } from 'context/snackbar';
import { TextareaAutosize, Tooltip } from '@mui/material';
import CronoEditor from './CronoEditor';
import ReactQuill from 'react-quill';

export interface ICronoEditorRichFieldModalProps {
  initialValue: string;
  label?: string;
  onSave: (value: string) => void;
  placeholder?: string;
  type: 'text' | 'html';
}

export function CronoEditorRichFieldModal({
  initialValue,
  label,
  onSave,
  type,
  placeholder,
}: ICronoEditorRichFieldModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [modalValue, setModalValue] = useState(initialValue);
  const editorRef = useRef<ReactQuill>(null);

  const { pushSnackbar } = useSnackbar();

  const inputComponent = useMemo(() => {
    return {
      html: (
        <CronoEditor
          className="crono-editor-rich-field"
          hideStylingButtons
          hideToolbar
          editorRef={editorRef}
          value={modalValue}
          onChange={setModalValue}
          placeholder={placeholder}
        />
      ),
      text: (
        <TextareaAutosize
          style={{
            height: '100%',
            border: 'none',
            resize: 'none',
            outline: 'none',
            fontFamily: 'Poppins, sans-serif',
          }}
          className={'force-overflow-auto'}
          placeholder="Enter a text"
          value={modalValue}
          onChange={(ev) => setModalValue(ev.target.value)}
        />
      ),
    }[type];
  }, [type, placeholder, modalValue]);

  useEffect(() => {
    setModalValue(initialValue);
  }, [initialValue, isOpen]);

  function handleSave() {
    onSave(modalValue);
    setIsOpen(false);
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(modalValue);
      pushSnackbar({ message: 'Copied to clipboard', severity: 'success' });
    } catch (e) {
      console.error('Failed to copy to clipboard', e);
      pushSnackbar({
        message: 'Failed to copy to clipboard',
        severity: 'error',
      });
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        sx={{ zIndex: 1500 }}
      >
        <div className="bg-white rounded-2xl w-lg absolute top-1/2 left-1/2 -translate-1/2 p-10 font-sans flex flex-col gap-8">
          <h1 className="text-2xl font-bold text-gray-900 m-0">
            {label ?? 'Edit'}
          </h1>

          <div className="flex flex-col h-80 max-h-80 overflow-hidden gap-1 border border-gray-200 rounded-lg p-[5px] pr-0">
            {inputComponent}

            <div className="flex justify-end pr-[5px]">
              <Tooltip title="Copy" enterDelay={400} enterNextDelay={400} arrow>
                <div className="grid">
                  <Button
                    className="p-0 size-8 rounded-full"
                    variant="ghost"
                    tone="gray"
                    size="md"
                    onClick={handleCopy}
                  >
                    <IconCopy size={24} color="currentColor" />
                  </Button>
                </div>
              </Tooltip>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              className="min-w-30"
              variant="outline"
              color="gray"
              size="lg"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>

            <Button className="min-w-30" size="lg" onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <Tooltip title="Expand" enterDelay={400} enterNextDelay={400} arrow>
        <div className="max-h-6">
          <Button
            className="p-0 size-6 rounded-full"
            variant="ghost"
            tone="gray"
            size="md"
            onClick={() => setIsOpen(true)}
          >
            <IconExpand2 size={18} />
          </Button>
        </div>
      </Tooltip>
    </>
  );
}
